'use client';

import { Loader2, StickyNote, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { richTextPlainText, sanitizeRichText } from '../../lib/rich-text';
import { type RouterInputs, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { TiptapEditor } from '../comments/tiptap-editor';
import { type Note, NoteCard } from './NoteCard';

/** `'contact' | 'anchor'`, read off the router rather than redeclared. Adding one is a backend change. */
export type NoteEntityType = RouterInputs['notes']['list']['entityType'];

export interface NotesPanelProps {
  /** What the notes hang off. The panel does the rest — fetching, authoring, pinning, deleting. */
  entityType: NoteEntityType;
  entityId: number;
  isReadOnly?: boolean;
  /**
   * Where the composer sits relative to the list. `top` is a detail page's notes tab; `bottom`
   * pins it to the foot of a full-height column, which is how the browser extension's side panel
   * reads. Layout only — the composer itself is identical either way.
   */
  composerPosition?: 'top' | 'bottom';
  placeholder?: string;
  /** Replaces the default "no notes yet" block — the wording is the only per-entity copy here. */
  emptyState?: ReactNode;
  /** Host cache invalidation after any write (a contact's activity feed, say). */
  onChanged?: () => void;
  className?: string;
}

/**
 * Every note list, on every surface.
 *
 * One implementation for a contact's notes tab and the extension's page notes: the same card, the
 * same composer, the same pinning, the same delete confirmation. It owns its own data through the
 * `notes` router, so a host supplies an entity reference and nothing else — there is no callback
 * for a host to answer "what does adding a note do" differently.
 *
 * This is a *container*, the same axis as `TaskPanel`, not a fact renderer. `NoteCard` is the
 * renderer. See ADR-0009.
 */
export function NotesPanel({
  entityType,
  entityId,
  isReadOnly = false,
  composerPosition = 'top',
  placeholder = 'Capture a useful detail…',
  emptyState,
  onChanged,
  className,
}: NotesPanelProps) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState('');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingNote, setDeletingNote] = useState<Note | null>(null);

  const entityRef = { entityType, entityId };

  const { data: notes = [], isLoading } = trpc.notes.list.useQuery(entityRef, {
    enabled: entityId > 0,
  });
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();

  // Switching entity while a draft or an edit is open would otherwise carry it across. Reset
  // during render rather than in an effect: React's own documented pattern for state derived from
  // a prop change, and it avoids a frame where the previous entity's draft is shown.
  const entityKey = `${entityType}:${entityId}`;
  const [renderedEntityKey, setRenderedEntityKey] = useState(entityKey);
  if (renderedEntityKey !== entityKey) {
    setRenderedEntityKey(entityKey);
    setDraft('');
    setEditingNote(null);
    setEditContent('');
  }

  const afterWrite = async () => {
    await utils.notes.list.invalidate(entityRef);
    onChanged?.();
  };

  const createMutation = trpc.notes.create.useMutation({
    onSuccess: async () => {
      await afterWrite();
      toast.success('Note added');
    },
    onError: (error, variables) => {
      setDraft(variables.content);
      toast.error(error.message || 'Could not add note');
    },
  });

  const updateMutation = trpc.notes.update.useMutation({
    onSuccess: async () => {
      await afterWrite();
    },
    onError: (error) => toast.error(error.message || 'Could not update note'),
  });

  const deleteMutation = trpc.notes.delete.useMutation({
    onSuccess: async () => {
      setDeletingNote(null);
      await afterWrite();
      toast.success('Note deleted');
    },
    onError: (error) => toast.error(error.message || 'Could not delete note'),
  });

  // The editor clears itself on submit, so the draft is cleared optimistically and restored by
  // the error handler rather than held until the mutation resolves.
  const submitDraft = (content: string) => {
    if (!richTextPlainText(content) || createMutation.isPending) return;
    setDraft('');
    createMutation.mutate({ ...entityRef, content });
  };

  // Rows written before sanitizing existed can hold markup the editor never emits; clean it on
  // the way in so saving an old note cannot round-trip the payload.
  const startEditing = (note: Note) => {
    setEditingNote(note);
    setEditContent(sanitizeRichText(note.content));
  };

  const saveEdit = () => {
    if (!editingNote || !richTextPlainText(editContent) || updateMutation.isPending) return;
    updateMutation.mutate(
      { id: editingNote.id, content: editContent },
      {
        onSuccess: () => {
          setEditingNote(null);
          setEditContent('');
          toast.success('Note updated');
        },
      }
    );
  };

  const togglePin = (note: Note) =>
    updateMutation.mutate({ id: note.id, isPinned: !note.isPinned });

  const composer = !isReadOnly && (
    <div className={cn('shrink-0', composerPosition === 'top' ? 'pb-4' : 'pt-3')}>
      <TiptapEditor
        key={`note-draft-${entityType}-${entityId}`}
        value={draft}
        onChange={setDraft}
        mode="note"
        minRows={composerPosition === 'bottom' ? 2 : 3}
        maxRows={composerPosition === 'bottom' ? 6 : 8}
        users={members}
        placeholder={placeholder}
        onSubmit={submitDraft}
        submitLabel={createMutation.isPending ? 'Adding…' : 'Add note'}
      />
    </div>
  );

  const list = isLoading ? (
    <div className="flex justify-center py-10 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  ) : notes.length === 0 ? (
    (emptyState ?? (
      <div className="rounded-xl border border-dashed border-border p-7 text-center">
        <StickyNote className="mx-auto h-8 w-8 text-muted-foreground/50" />
        <p className="mt-2 text-xs font-semibold text-foreground">No notes yet</p>
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          {isReadOnly
            ? 'Nobody has captured a note here yet.'
            : 'Keep decisions, preferences, and context close by.'}
        </p>
      </div>
    ))
  ) : (
    <div className="space-y-3">
      {notes.map((note) =>
        editingNote?.id === note.id ? (
          <article key={note.id} className="rounded-xl border border-primary/40 bg-card p-3">
            <TiptapEditor
              value={editContent}
              onChange={setEditContent}
              mode="note"
              minRows={3}
              maxRows={10}
              users={members}
              autoFocus
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={() => setEditingNote(null)}
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={!richTextPlainText(editContent) || updateMutation.isPending}
                onClick={saveEdit}
              >
                {updateMutation.isPending && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Save changes
              </Button>
            </div>
          </article>
        ) : (
          <NoteCard
            key={note.id}
            note={note}
            isReadOnly={isReadOnly}
            onEdit={startEditing}
            onTogglePin={togglePin}
            onDelete={setDeletingNote}
          />
        )
      )}
    </div>
  );

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      {composerPosition === 'top' && composer}
      <div className={cn(composerPosition === 'bottom' && 'min-h-0 flex-1 overflow-y-auto')}>
        {list}
      </div>
      {composerPosition === 'bottom' && composer}

      <AlertDialog open={!!deletingNote} onOpenChange={(open) => !open && setDeletingNote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              The note is removed for everyone in the workspace. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep note</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={() => deletingNote && deleteMutation.mutate({ id: deletingNote.id })}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
